import * as Dialog from '@radix-ui/react-dialog';
import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import { Field, FormError, Label } from '@/components/ui/field';
import { RequiredFieldsNote } from '@/components/ui/form-section';
import { useCreateLead } from '@/hooks/leads';
import { ApiError } from '@/lib/api';
import { useToast } from '@/lib/toast-context';
import { LEAD_SOURCE_LABELS, type LeadDetail } from '@shared/schemas/lead';

/**
 * Write down an enquiry that did not come through the website.
 *
 * **The gap this fills is a phone call.** Every lead in the system arrived
 * through the quote form or the contact form, because those were the only two
 * things that could write one — so somebody who rang the office went into a
 * notebook, or nowhere.
 *
 * **One name field, matching both web forms.** `lead.first_name` holds the whole
 * name and the split into first and last happens once, by a person, at the
 * moment of conversion. Asking an admin to split it while a customer is still on
 * the phone is asking them to guess.
 *
 * **The email is optional and the phone number is the alternative.** This is the
 * P9 rule again: requiring an address from somebody who rang up does not produce
 * an address, it produces `noemail@…`, and the conversion path would then try to
 * send a welcome message to it. One of the two is required — by the form, by the
 * service, and by `lead_contactable_chk` in the database.
 */

/** The office's own four. The website's two are not offerable — see the route. */
const SOURCES = ['phone', 'walk_in', 'referral', 'manual'] as const;

export function AddLeadDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (created: LeadDetail) => void;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-ink-300 bg-white p-6 shadow-pop">
          {/*
            Keyed on `open`, so reopening the dialog starts empty rather than
            holding the last enquiry somebody typed. A fresh mount is a fresh
            `useState`; a reset effect is four lines that can each be forgotten.
          */}
          <AddLeadForm
            key={String(open)}
            onCreated={onCreated}
            onDone={() => {
              onOpenChange(false);
            }}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function AddLeadForm({
  onDone,
  onCreated,
}: {
  onDone: () => void;
  onCreated?: (created: LeadDetail) => void;
}) {
  const toast = useToast();
  const create = useCreateLead();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [city, setCity] = useState('');
  const [source, setSource] = useState<(typeof SOURCES)[number]>('phone');
  const [notes, setNotes] = useState('');

  const error = create.error instanceof ApiError ? create.error : null;

  /*
   * Checked here as well as on the server, so the person filling this in gets
   * told before they press the button rather than after. The server and the
   * database both enforce it too — this is the courtesy, not the rule.
   */
  const reachable = email.trim().length > 0 || phone.trim().length > 0;

  function submit(event: FormEvent) {
    event.preventDefault();

    create.mutate(
      { name, email, phone, businessName, city, source, notes },
      {
        onSuccess: (created) => {
          toast.success('Enquiry recorded.');
          onCreated?.(created);
          onDone();
        },
      },
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <Dialog.Title className="text-h3 text-ink-900">Record an enquiry</Dialog.Title>
      <Dialog.Description className="text-meta text-ink-500">
        Somebody who rang, was referred, or walked in. Website enquiries arrive here on their own.
      </Dialog.Description>

      {error ? <FormError>{error.message}</FormError> : null}

      <Field
        label="Name"
        name="name"
        required
        value={name}
        onChange={(event) => {
          setName(event.target.value);
        }}
        hint="However they gave it. It is split into first and last when they become a customer."
      />

      <Field
        label="Business"
        name="businessName"
        value={businessName}
        onChange={(event) => {
          setBusinessName(event.target.value);
        }}
        hint="If they are ringing on behalf of one."
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Email"
          name="email"
          type="email"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
        />
        <Field
          label="Phone"
          name="phone"
          type="tel"
          value={phone}
          onChange={(event) => {
            setPhone(event.target.value);
          }}
        />
      </div>

      {/*
        Not an error until they try to save — it is the ordinary state of a
        half-filled form, and turning it red while somebody is still typing the
        first field is nagging rather than helping.
      */}
      <p className="text-meta text-ink-500">
        One of the two is enough. A phone enquiry does not need an email address.
      </p>

      <Field
        label="Town"
        name="city"
        value={city}
        onChange={(event) => {
          setCity(event.target.value);
        }}
      />

      <div className="space-y-1.5">
        <Label htmlFor="lead-source">How did they get in touch?</Label>
        <select
          id="lead-source"
          name="source"
          value={source}
          onChange={(event) => {
            setSource(event.target.value as (typeof SOURCES)[number]);
          }}
          className="min-h-touch w-full rounded-sm border border-ink-300 bg-white px-3 text-body"
        >
          {SOURCES.map((value) => (
            <option key={value} value={value}>
              {/* The label, never the stored value — `walk_in` must not reach a
                  screen. P5. */}
              {LEAD_SOURCE_LABELS[value]}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="lead-notes">What did they want?</Label>
        <textarea
          id="lead-notes"
          name="notes"
          rows={4}
          value={notes}
          onChange={(event) => {
            setNotes(event.target.value);
          }}
          className="w-full rounded-sm border border-ink-300 bg-white px-3 py-2 text-body"
          placeholder="Three-bed off Main Street, wants a deep clean before a move-out. Asked for a price by Friday."
        />
      </div>

      <RequiredFieldsNote />

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onDone}>
          Cancel
        </Button>
        <Button type="submit" disabled={create.isPending || name.trim().length === 0 || !reachable}>
          {create.isPending ? 'Saving…' : 'Record enquiry'}
        </Button>
      </div>
    </form>
  );
}
